/**
 * Victory Church Bratislava — brand theme (runtime layer).
 *
 * SOURCE OF TRUTH = brand/tokens.json. This module is a hand-kept RUNTIME SUBSET
 * for code (Remotion scenes, generators) — it intentionally does NOT mirror every
 * token (provenance/notes/full safe-zones/export live in tokens.json + DESIGN-CODE.md).
 * If they disagree, tokens.json wins. Keep changes in sync with tokens.json.
 */

// — OFFICIAL palette (brandbook — only these three are "brand colors") —
export const OFFICIAL = { blue: "#1C8CD9", white: "#FFFFFF", black: "#1D1C20" } as const;

// — DERIVED tints/surfaces (media depth only — NOT brand colors, use sparingly) —
export const DERIVED = {
  blue100: "#BFE0F5",
  blue300: "#5BAEE6",
  blue700: "#156FB0",
  blue900: "#0E4A77",
  ink800: "#26252B",
  ink700: "#33323A",
  lightGray: "#F5F5F5",
} as const;

export const COLOR = { ...OFFICIAL, ...DERIVED } as const;

// Light backgrounds where foreground MUST be dark for legibility.
const LIGHT_BG = new Set<string>([OFFICIAL.white, DERIVED.lightGray, DERIVED.blue100, DERIVED.blue300]);

// — Official typography —
export const FONT = {
  display: "JetBrains Mono", // ExtraBold 800 / Regular 400 — headings + SHORT blocks
  text: "TT Norms Pro", // Bold / Regular — large text (commercial; fallback below)
  fallback: "Roboto", // Regular / Bold — fallback + best choice for subtitles/long text
} as const;

export const WEIGHT = { regular: 400, medium: 500, bold: 700, extrabold: 800 } as const;

// — Type scale (base @1920×1080). Use perFormatScale(), NOT raw height ratio. —
export const TYPE = {
  display: { size: 96, weight: 800, family: FONT.display, lineHeight: 1.1, tracking: -1 },
  quote: { size: 64, weight: 800, family: FONT.display, lineHeight: 1.28, tracking: -1 },
  title: { size: 56, weight: 800, family: FONT.display, lineHeight: 1.15, tracking: 0 },
  subtitle: { size: 38, weight: 400, family: FONT.display, lineHeight: 1.3, tracking: 0 },
  body: { size: 32, weight: 400, family: FONT.fallback, lineHeight: 1.4, tracking: 0 },
  caption: { size: 26, weight: 400, family: FONT.fallback, lineHeight: 1.35, tracking: 0 },
} as const;

// — Canvas formats —
export const FORMAT = {
  youtube: { w: 1920, h: 1080 },
  reels: { w: 1080, h: 1920 },
  square: { w: 1080, h: 1080 },
  portrait: { w: 1080, h: 1350 },
  story: { w: 1080, h: 1920 },
} as const;

/**
 * Per-format type scale. Scales by the SHORTER side vs the 1080 base (not height),
 * so vertical formats don't blow up. Clamped to avoid extremes.
 */
export function perFormatScale(format: { w: number; h: number }): number {
  const base = 1080; // shorter side of 1920×1080
  const shorter = Math.min(format.w, format.h);
  return Math.max(0.7, Math.min(1.25, shorter / base));
}

// — Layout / safe zones (px reserved for platform UI on vertical) · derived —
export const LAYOUT = { marginPct: 8.5, maxTextWidthPct: 72 } as const;
export const SAFE_ZONE = {
  // 16:9 — title-safe
  youtube: { topPct: 5, bottomPct: 7, sidePct: 5 },
  // 9:16 — reserve platform UI (caption + action buttons + profile/header)
  reels: { topPx: 220, bottomPx: 520, rightPx: 180, leftPx: 40 },
  story: { topPx: 250, bottomPx: 250, sidePx: 40 },
} as const;

// — Motion (concrete: reproducible across scenes) · derived —
export const MOTION = {
  spring: { damping: 200, stiffness: 100, mass: 1 },
  fps: 30,
  enterFrames: 22,
  holdFrames: 90,
  exitFrames: 12,
  staggerFrames: 3,
  maxTransitionMs: 300,
} as const;

// — Subtitles (burn-in for social) · derived —
export const SUBTITLE = {
  font: FONT.fallback,
  weight: WEIGHT.medium,
  sizePx: { h16x9: 48, v9x16: 52 },
  maxLines: 2,
  maxCharsPerLine: 42,
  maxCps: 17,
  minDurationSec: 1,
  maxDurationSec: 7,
  color: OFFICIAL.white,
  plate: { color: OFFICIAL.black, opacity: 0.7 }, // semi-opaque chip for legibility over video
  outlinePx: 0, // use plate OR outline, not both
  speakerLabelColor: DERIVED.blue300,
} as const;

// — Component slots (px @1920×1080 base) · derived —
export const COMPONENT = {
  quote: { padding: 160, accentBarPx: 8, gap: 40, maxTextWidthPx: 1460 },
  lowerThird: { bottomPx: 150, leftPx: 96, namesize: 40, rolesize: 28, barColor: OFFICIAL.blue },
  announcement: { padding: 140, blockColor: OFFICIAL.blue, titlesize: 80 },
  verseCard: { padding: 130, refColor: DERIVED.blue300, formats: ["square", "portrait"] },
} as const;

// — Sub-brands (observed; colors sampled from real assets, fonts = stand-ins) —
export const SUBBRAND = {
  women: {
    sky: ["#E1E8E8", "#B8C8D2", "#9EAFBD"], // light watercolor sky (sampled)
    skyText: "#3A4A5A",
    gold: "#C2A462", // sampled gold accent
    goldLight: "#CEBA8A",
    serif: "Playfair Display", // Cyrillic serif stand-in for the gold serif
    script: "Dancing Script", // stand-in for the cursive "Victory" logo
  },
  worship: {
    blue: ["#254E96", "#4D72B4"], // royal blue gradient (sampled — NOT brand cyan #1C8CD9)
    text: "#FFFFFF",
    mono: "JetBrains Mono",
    script: "Dancing Script", // stand-in for the "Worship" brush lettering
  },
} as const;

// — Logo —
export const LOGO = {
  markAsset: "victory-mark.svg",
  markIsSingleColor: true, // #1C8CD9 disc with a TRANSPARENT cross cut-out (cross = hole, not white fill)
  clearSpace: "3H (H = высота «I»)",
  watermark: { sizePctOfHeight: 5.5, opacity: 0.9, position: "bottom-right" },
  // ⚠️ Works on DARK/SOLID bg only: filter recolors the disc white, cross shows the bg through.
  // On photo/busy bg → place mark on a solid chip, or use blue (light bg) / white-on-disc lockup.
  whiteOnDarkFilter: "brightness(0) invert(1)",
} as const;

/** Legible foreground for a brand background (fixes earlier white-on-light bug). */
export function onColor(bg: string): string {
  return LIGHT_BG.has(bg) ? OFFICIAL.black : OFFICIAL.white;
}

/**
 * Contrast reference (computed, WCAG). Honest labels — NOT all are AA for normal text.
 *  white/#1C8CD9 = 3.62 → LARGE text only · white/#1D1C20 = 16.95 (AAA)
 *  black/#F5F5F5 = 15.54 (AAA) · #1C8CD9/#1D1C20 = 4.68 (AA) · white/#156FB0 = 5.34 (AA)
 *  black/#BFE0F5 = 12.26 · black/#5BAEE6 = 6.97. For body text need ≥4.5 — see DESIGN-CODE §1.
 */
export const CONTRAST_NOTE = "white-on-blue = large-text only (3.62:1); body text needs ≥4.5 — use white-on-dark, blue-on-dark, or dark-on-light.";

export const BRAND = {
  OFFICIAL, DERIVED, COLOR, FONT, WEIGHT, TYPE, FORMAT, LAYOUT, SAFE_ZONE,
  MOTION, SUBTITLE, COMPONENT, LOGO, perFormatScale, onColor, CONTRAST_NOTE,
};
export default BRAND;

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
